import {
  type UseMutateAsyncFunction,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysCostCenter } from './queryKeys';

type CostCenterData = Record<string, unknown>;

export type MutationCostCenterArguments = {
  costCenterId: UUID;
  costCenterData: CostCenterData;
};

type MutationCostCenterResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

export type UpdateCostCenterMutateAsyncFunction = UseMutateAsyncFunction<
  MutationCostCenterResponse,
  Error,
  MutationCostCenterArguments
>;

type UpdateCostCenterMutationOptions = UseMutationOptions<
  MutationCostCenterResponse,
  Error,
  MutationCostCenterArguments
>;

/**
 * Updates a given cost center.
 *
 * @param {string} costCenterId - The ID of the cost center to update.
 * @param {Object} costCenterData - An object containing the cost center data to update.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Accounting-Reference/operation/update_accounting_reference_accounting_reference__acc_ref_uuid__put
 */
export const updateCostCenterData = async (
  costCenterId: UUID,
  costCenterData: CostCenterData,
) => {
  const response = await vestigasApi
    .put(ENDPOINT.COST_CENTER.UPDATE(costCenterId), {
      json: costCenterData,
    })
    .json<MutationCostCenterResponse>();

  return response;
};

/**
 * Custom hook for updating a given cost center.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */

export const useMutationUpdateCostCenter = (
  options?: UpdateCostCenterMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      costCenterId,
      costCenterData,
    }: MutationCostCenterArguments) =>
      updateCostCenterData(costCenterId, costCenterData),
    onSuccess: async (_data, { costCenterId }) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysCostCenter.get(costCenterId),
      });
    },
    ...options,
  });
};
